import { Link } from 'react-router-dom'
import { AlertCircle, MessageCircle, Clock3 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { useSubscriptionPayments } from '@/hooks/useSubscriptionPayments'

const PLAN_LABELS = {
  MONTHLY_STANDARD: 'Standard',
  YEARLY_ECO: 'Eco',
  LIFETIME_PREMIUM: 'Premium',
} as const

export default function PendingPlanBanner() {
  const { data, isLoading } = useSubscriptionPayments(10)

  if (isLoading || !data?.length) return null

  const pending = data.find((p) => p.status === 'PENDING')
  if (!pending) return null

  const label = PLAN_LABELS[pending.plan] ?? pending.plan
  const amount = new Intl.NumberFormat('fr-FR').format(pending.amountCfa)

  const whatsappNumber = '2250709149747'
  const whatsappText = encodeURIComponent(
    `Bonjour, j’ai effectué le paiement pour le plan ${label}. Voici ma preuve de transaction.`
  )

  return (
    <Card className='border-amber-200 bg-amber-50/70 shadow-sm dark:border-amber-900 dark:bg-amber-950/20'>
      <CardContent className='p-4 md:p-5'>
        <div className='flex flex-col gap-4 md:flex-row md:items-start md:justify-between'>
          <div className='flex gap-3'>
            <div className='mt-0.5 rounded-full bg-amber-100 p-2 dark:bg-amber-900/40'>
              <AlertCircle className='h-5 w-5 text-amber-700 dark:text-amber-300' />
            </div>

            <div>
              <div className='flex flex-wrap items-center gap-2'>
                <h3 className='font-semibold text-amber-900 dark:text-amber-100'>
                  Activation de votre plan en attente
                </h3>
                <Badge
                  variant='outline'
                  className='border-amber-300 text-amber-800 dark:border-amber-700 dark:text-amber-200'
                >
                  {label}
                </Badge>
              </div>

              <p className='mt-2 text-sm text-amber-900/90 dark:text-amber-100/90'>
                Votre demande d’abonnement a bien été enregistrée pour{' '}
                <strong>{amount} CFA</strong>. Nous vérifions votre paiement
                avant d’activer votre compte.
              </p>

              <p className='mt-2 text-sm text-amber-800/90 dark:text-amber-200/90'>
                Nous nous excusons pour cette étape supplémentaire. C’est une
                solution temporaire mise en place en attendant l’intégration
                complète des APIs de paiement.
              </p>

              <div className='mt-3 flex items-center gap-2 text-xs text-amber-800 dark:text-amber-200'>
                <Clock3 className='h-4 w-4' />
                <span>
                  L’activation se fait après vérification de votre preuve.
                </span>
              </div>
            </div>
          </div>

          <div className='flex w-full flex-col gap-2 md:w-auto md:min-w-[260px]'>
            <a
              href={`https://wa.me/${whatsappNumber}?text=${whatsappText}`}
              target='_blank'
              rel='noopener noreferrer'
            >
              <Button className='w-full'>
                <MessageCircle className='mr-2 h-4 w-4' />
                Envoyer ma preuve
              </Button>
            </a>

            <Link
              to={`/billing/manual-payment?plan=${encodeURIComponent(
                pending.plan
              )}`}
            >
              <Button variant='outline' className='w-full'>
                Voir les instructions
              </Button>
            </Link>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
